import { JSX } from "preact";
import { useState, useEffect, useCallback, useRef } from "preact/hooks";

import { ChatTransport, isExpiredSessionError, type CloseReason, type PrechatValues } from "../transport";
import { MessageList } from "./MessageList";
import { Composer } from "./Composer";
import { ChoiceChips } from "./ChoiceChips";
import { LeadForm, type LeadFormValues } from "./LeadForm";
import {
  loadChat,
  saveChat,
  loadEndedSession,
  markSessionEnded,
  loadPrechatContact,
  savePrechatContact,
  wasCapturedThisTab,
  markCapturedThisTab,
  clearCapturedThisTab,
  markSubmitInFlight,
  clearSubmitInFlight,
  isSubmitInFlight,
  type PrechatContact,
} from "../chat-persistence";
import type { Message } from "../widget";

// crypto.randomUUID is secure-context (https) only — fall back so the widget
// never crashes on a plain-http host page (where it's undefined).
function uid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Pre-chat gate intro (theme.form_intro overrides). Neutral wording — the
// platform serves every practice area, not just injury.
const DEFAULT_FORM_INTRO =
  "Enter your name and number and tell us a bit about what you need — we'll take it from there.";

// Ended-sheet copy per close_reason (persona doctrine: the ONLY visitor-facing
// promise is next business day — no "as soon as possible", no urgency).
function endedCopyFor(reason: CloseReason, firm: string): string {
  if (reason === "referred") return "Thanks — your note's been passed to the team.";
  if (reason === "ended") return "This chat has ended.";
  return `A lawyer from ${firm} will contact you next business day.`;
}

// Recap line when the single-session lock is active but the ended transcript
// didn't survive storage — the sheet still renders over SOMETHING coherent.
const endedRecapMessage = (): Message => ({
  id: uid(),
  role: "system",
  text: "Your previous chat has ended.",
  voice: false,
});

// One explicit phase instead of interacting booleans — the intent-gated flow
// has real branching (form → connecting → chat | captured | back-to-form) and
// a phase union keeps unreachable combinations unrepresentable.
//
//   welcome     branded cover with Start chat + starter chips (as before)
//   form        the pre-chat LeadForm ("gate") or outage LeadForm ("outage")
//   connecting  covers REAL work now: triage + contextual first-reply generation
//   chat        live conversation (composer, choices, ended state)
//   captured    submission stored as a contact-form lead; confirmation screen
type Phase = "welcome" | "form" | "connecting" | "chat" | "captured";

interface Props {
  chatHost: string;
  firmName: string;
  /** Opaque per-firm widget key — the preferred firm identity sent to chat-service. */
  firmToken?: string;
  /** Display title for the header/connect notice (theme.header_title || firmName). */
  headerTitle?: string;
  /** Welcome-screen headline (theme.welcome_title). Falls back to "Chat with <firm>". */
  welcomeTitle?: string | null;
  /** Welcome-screen sub-line (theme.welcome_subtitle). */
  welcomeSubtitle?: string;
  /** Firm logo URL (theme.logo_url) for the header + hero avatar. */
  logoUrl?: string | null;
  /** Optional rep/headshot image (theme.avatar_url) — takes precedence over
   *  the logo for the header + welcome hero so the chat feels human. */
  avatarUrl?: string | null;
  /** Up to 3 configurable trust badges (theme.trust_chips). */
  trustChips?: string[];
  /** Up to 4 tappable starter prompts (theme.starter_prompts). */
  starterPrompts?: string[];
  /** Intent gate (theme.prechat_enabled): pre-chat form before any chat.
   *  False → legacy flow, Start chat goes straight to the greeting. */
  prechatEnabled?: boolean;
  /** Pre-chat form intro line (theme.form_intro). Empty → neutral default. */
  formIntro?: string;
  demoToken?: string;
  /** Source/attribution (contract v1.2), captured at widget load. */
  source?: import("../source").SourceInfo;
  /** Inline (in-page hero) mount — no close ×; onClose restarts the panel. */
  inline?: boolean;
  onClose: () => void;
}

export function ChatWindow({ chatHost, firmName, firmToken, headerTitle, welcomeTitle, welcomeSubtitle, logoUrl, avatarUrl, trustChips, starterPrompts, prechatEnabled, formIntro, demoToken, source, inline, onClose }: Props): JSX.Element {
  const title = headerTitle?.trim() || firmName;
  // Header shows a name-free "Live Assistant" label by default; a firm can set
  // header_title to show their own name instead. (title stays = firm name for the
  // ended-state copy + avatar initials.)
  const headerLabel = headerTitle?.trim() || "Live Assistant";
  // No rep NAME in widget chrome by owner decision — the connected notice and
  // nudge say "live assistant" without naming a persona. (The AI's own persona
  // name still comes from Settings → rep name, server-side.)
  // Welcome/cover copy. Firm-customizable via theme (welcome_title/subtitle).
  // Defaults are generic (no firm name templated into the popup).
  const welcomeHeading = welcomeTitle?.trim() || "Legal Inquiries?";
  const welcomeSub = welcomeSubtitle?.trim() || "We're here to help, connect with a live agent.";
  // Brand initials for the avatar/hero when no logo is set: first two letters of
  // the firm name (e.g. "NG Sidhu Law" → "NG").
  const initials = (title.replace(/[^a-zA-Z]/g, "").slice(0, 2) || "AC").toUpperCase();
  const logo = logoUrl?.trim() || null;
  // Avatar precedence: explicit rep headshot → firm logo → initials. The rep
  // headshot makes the conversation feel like a real person, not a bot.
  const avatar = avatarUrl?.trim() || null;
  const heroImg = avatar || logo;
  const chips = (trustChips ?? []).filter((c) => c.trim());
  const starters = (starterPrompts ?? []).filter((s) => s.trim());
  const gate = prechatEnabled !== false; // default on
  const transport = useRef(new ChatTransport(chatHost));
  const rootRef = useRef<HTMLDivElement>(null);

  // Single session per visitor: a session that ENDED within the last 24h
  // (SESSION_ENDED_TTL_MS) locks the widget into the ended view of that
  // conversation — no new session, no pre-chat form — until the lock expires.
  // Demo/test mode skips the lock so dashboard testing always starts fresh.
  const priorEnded = useRef(demoToken ? null : loadEndedSession(firmName)).current;

  // Restore an in-progress chat (closed bubble or page reload) so the visitor
  // returns exactly where they left off. null if none/stale/ended.
  // In demo/test mode (dashboard preview) we DON'T restore — a refresh should
  // reset cleanly to the cover screen so testing always starts fresh.
  const restored = useRef(demoToken || priorEnded ? null : loadChat(firmName)).current;
  const canResume = !!restored && restored.messages.length > 0;

  const [sessionId, setSessionId] = useState<string | null>(restored?.sessionId ?? null);
  const [messages, setMessages] = useState<Message[]>(() => {
    if (priorEnded) {
      return priorEnded.messages.length > 0 ? priorEnded.messages : [endedRecapMessage()];
    }
    return canResume ? restored!.messages : [];
  });
  const [loading, setLoading] = useState(false);
  // True while the arrival pacing beats play (welcome → typing → first reply).
  // Locks the composer so a fast typer can't send a message that renders
  // above the greeting and races the pacing sequence's loading toggles.
  const [pacing, setPacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Text of the last send that failed — drives the inline "Try again" button.
  const [retryText, setRetryText] = useState<string | null>(null);
  const [currentChoices, setCurrentChoices] = useState<
    | { prompt: string; choices: string[]; allow_free_text: boolean }
    | null
  >(null);
  const [ended, setEnded] = useState(!!priorEnded);
  // Why the session ended — set from the wire on close (absent ⇒ "callback",
  // the next-business-day promise) and restored with a prior ended session.
  const [closeReason, setCloseReason] = useState<CloseReason | null>(priorEnded?.close_reason ?? null);

  // Phase machine. A prior ENDED session mounts straight into its ended view
  // (single session per visitor); a resumed chat skips everything; a same-tab
  // visitor whose submission was already captured sees the confirmation, not a
  // fresh form (prevents accidental duplicate submissions; "Send another
  // message" resets it). A submit left in flight by a closed-and-reopened
  // bubble is ADOPTED: we hold "connecting" and poll for its terminal markers
  // (see the effect below) instead of showing a fresh welcome that invites a
  // duplicate.
  const [phase, setPhase] = useState<Phase>(() => {
    if (priorEnded) return "chat";
    if (canResume) return "chat";
    if (!demoToken && wasCapturedThisTab(firmName)) return "captured";
    if (!demoToken && isSubmitInFlight(firmName)) return "connecting";
    return "welcome";
  });
  const [formVariant, setFormVariant] = useState<"gate" | "outage">("gate");
  // External (network-path) error surfaced inside the form.
  const [formError, setFormError] = useState<string | null>(null);
  // Values preserved across the degrade ladder so a failed submit never makes
  // the visitor retype.
  const formValuesRef = useRef<LeadFormValues | null>(null);
  // Starter-chip text prefills the form's inquiry field (the gate stays
  // universal — no path into chat without name+phone).
  const [starterPrefill, setStarterPrefill] = useState("");
  // Saved contact (name/phone, 30d) prefills a returning visitor's form.
  // State (not a mount ref) so "Send another message" re-reads a contact
  // saved AFTER mount — a first-time visitor's second inquiry prefills too.
  const [savedContact, setSavedContact] = useState<PrechatContact | null>(
    () => (demoToken ? null : loadPrechatContact(firmName)),
  );
  // True once THIS instance started a submit — distinguishes our own
  // connecting phase from an adopted orphan's.
  const ownSubmitRef = useRef(false);

  // Guards setState after unmount (closing the bubble). Network sends still
  // run to completion — a collected lead is captured even if the UI is gone.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  // Progressive connecting copy: after ~7s the line acknowledges the wait
  // (the model is genuinely reading their inquiry).
  const [connectingSlow, setConnectingSlow] = useState(false);
  useEffect(() => {
    if (phase !== "connecting") {
      setConnectingSlow(false);
      return;
    }
    const t = window.setTimeout(() => setConnectingSlow(true), 7000);
    return () => clearTimeout(t);
  }, [phase]);

  // Adopted orphan submit: a prior instance's prechat POST is still in flight
  // (bubble closed mid-connect, then reopened). Poll for the terminal markers
  // that handler writes — captured flag, or a persisted chat — and fall back
  // to the welcome screen once the marker expires (handler died with the page).
  useEffect(() => {
    if (phase !== "connecting" || ownSubmitRef.current || demoToken) return;
    const t0 = Date.now();
    const iv = window.setInterval(() => {
      if (wasCapturedThisTab(firmName)) {
        window.clearInterval(iv);
        setPhase("captured");
        return;
      }
      // The orphan handler may have landed a FIRST-TURN agent close — the
      // session is already ended; adopt its ended view (never a fresh welcome,
      // which would invite a second session).
      const done = loadEndedSession(firmName);
      if (done) {
        window.clearInterval(iv);
        setMessages(done.messages.length > 0 ? done.messages : [endedRecapMessage()]);
        setCloseReason(done.close_reason);
        setEnded(true);
        setPhase("chat");
        return;
      }
      const p = loadChat(firmName);
      if (p && p.messages.length > 0) {
        window.clearInterval(iv);
        setSessionId(p.sessionId);
        setMessages(p.messages);
        setPhase("chat");
        return;
      }
      if (!isSubmitInFlight(firmName) || Date.now() - t0 > 35_000) {
        window.clearInterval(iv);
        clearSubmitInFlight(firmName);
        setPhase("welcome");
      }
    }, 400);
    return () => window.clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, demoToken, firmName]);

  // Finalize the session (→ lead capture) only when the visitor LEAVES THE PAGE.
  // Closing the bubble no longer ends the session — the conversation persists
  // (see the save effect) so reopening resumes it. sendBeacon survives the
  // pagehide. (No unmount end-beacon: bubble-close = pause, not end.)
  useEffect(() => {
    if (!sessionId || ended) return;
    const onPageHide = (e: PageTransitionEvent) => {
      // bfcache entry (persisted=true): the page may come RIGHT BACK via the
      // back button with this chat still live on screen. Ending the server
      // session here would finalize a live conversation and turn the
      // returning visitor's next message into a duplicate lead. Skip — if
      // they never return, the idle reaper finalizes it as abandoned.
      if (e.persisted) return;
      transport.current.endSessionBeacon(sessionId, "navigated_away");
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [sessionId, ended]);

  // Escape closes the popup (standard dialog convention). Inline mode has no
  // close affordance, so it's a no-op there.
  useEffect(() => {
    if (inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inline, onClose]);

  // On open (bubble), move focus off the launcher into the dialog so keyboard/SR
  // users enter the conversation and the launcher's aria-hidden never holds
  // focus. The composer autofocuses once the chat is live.
  useEffect(() => {
    if (!inline) rootRef.current?.focus();
  }, [inline]);

  // Persist the text conversation so closing the bubble / reloading the page
  // returns the visitor to where they left off. An ENDED conversation persists
  // too — with the single-session marker — so reopening shows the closed chat
  // under the sheet, never a fresh session. Skipped in demo/test mode, and for
  // a RESTORED ended view (nothing changed; re-saving would refresh the ts that
  // anchors the 24h expiry).
  useEffect(() => {
    if (demoToken || phase !== "chat" || priorEnded) return;
    saveChat(firmName, { sessionId, messages, ended, close_reason: closeReason });
    if (ended) markSessionEnded(firmName, closeReason ?? "callback");
  }, [sessionId, messages, ended, closeReason, phase, firmName, demoToken, priorEnded]);

  // Ended-sheet geometry: the sheet overlays the bottom of the message list,
  // so the list gets bottom padding equal to the sheet's MEASURED height
  // (--acc-ended-h, consumed by `.acc-window--ended .acc-messages`) and the
  // scroll pins to the bottom once on mount — the last message is never hidden
  // under the sheet, and the history above stays scrollable. Re-measured on
  // resize (the sheet text re-wraps at other widths).
  const endedSheetRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ended || phase !== "chat") return;
    const root = rootRef.current;
    const measure = () => {
      const h = endedSheetRef.current?.offsetHeight ?? 0;
      root?.style.setProperty("--acc-ended-h", `${h}px`);
    };
    measure();
    // Reading scrollHeight after setting the var forces a reflow that already
    // includes the new padding, so this lands the last bubble above the sheet.
    const list = root?.querySelector<HTMLElement>(".acc-messages");
    if (list) list.scrollTop = list.scrollHeight;
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ended, phase]);

  const sendMessage = useCallback(
    async (text: string, voiceUsed = false, opts?: { echo?: boolean; sid?: string }) => {
      // sid override: callers that just created the session (arrival handlers)
      // pass it explicitly — their closure still sees the pre-create state.
      const activeSid = opts?.sid ?? sessionId;
      if (!activeSid || loading) return;
      setCurrentChoices(null);
      setError(null);
      setRetryText(null);

      // Add the user's message. Read receipts are intentionally omitted —
      // the chat bubble itself is sufficient acknowledgment that the
      // message was sent. On a retry the bubble is already on screen, so we
      // skip echoing it again (opts.echo === false).
      if (opts?.echo !== false) {
        const userMsgId = uid();
        setMessages((m) => [
          ...m,
          { id: userMsgId, role: "user", text, voice: voiceUsed },
        ]);
      }

      // Show typing indicator after a short delay (650ms). It stays
      // visible through the whole stream — the visitor doesn't see text
      // appearing word-by-word. Instead, the full reply pops in as one
      // bubble when the model is done, which matches how a real person
      // would type out a message then send it.
      let typingTimer: number | null = window.setTimeout(() => {
        typingTimer = null;
        setLoading(true);
      }, 650);

      const clearTypingTimer = () => {
        if (typingTimer !== null) {
          clearTimeout(typingTimer);
          typingTimer = null;
        }
      };

      // Accumulate the full reply silently. Don't render it incrementally
      // (the previous per-token streaming felt over-animated). We render
      // the final bubble once when the stream is done.
      let fullReply = "";
      const onDelta = (delta: string) => {
        if (delta) fullReply += delta;
      };

      let result = await transport.current.streamTurn(
        activeSid,
        text,
        voiceUsed,
        { onTextDelta: onDelta },
      );

      // A resumed conversation's server session may have EXPIRED (idle timeout)
      // or been finalized. ONLY in that case do we transparently start a fresh
      // session and retry once, so a returning visitor never hits a dead chat.
      // For transient errors (503/429/network) we must NOT reset the session —
      // that would drop the conversation history and double-post the lead. We
      // leave `result` as the error and surface it to the visitor below.
      // NOTE: this retry is a BARE startSession on purpose — no prechat block —
      // so the server takes the legacy Path A and never re-triages a live visitor.
      if (!result.ok && isExpiredSessionError(result.code)) {
        const fresh = await transport.current.startSession(firmName, firmToken, window.location.href, demoToken);
        if (fresh.ok) {
          setSessionId(fresh.value.session_id);
          fullReply = "";
          result = await transport.current.streamTurn(fresh.value.session_id, text, voiceUsed, {
            onTextDelta: onDelta,
          });
        }
      }

      clearTypingTimer();

      // Render the complete reply as a single bubble that lands all at
      // once with the entry animation. If the model returned final_text
      // in the done event, prefer that (it's the canonical / cleaned
      // version); otherwise use what we accumulated from deltas.
      const finalText = (result.ok ? result.value.final_text : "") || fullReply;
      if (finalText) {
        setMessages((m) => [
          ...m,
          {
            id: uid(),
            role: "assistant",
            text: finalText,
            voice: false,
          },
        ]);
      }
      setLoading(false);

      if (!result.ok) {
        setError(result.error);
        setRetryText(text); // enable the inline "Try again"
        return;
      }

      if (result.value.choices) {
        setCurrentChoices(result.value.choices);
      }
      if (result.value.ended) {
        setCloseReason(result.value.close_reason ?? "callback");
        setEnded(true);
      }
    },
    [sessionId, loading],
  );

  // ── Legacy arrival (gate disabled): bare session + server greeting ─────────
  const pendingFirstMsg = useRef<string | null>(null);
  const startBareSession = useCallback(async () => {
    const minConnect = 1400; // ms — hold the "connecting" beat a touch
    const t0 = Date.now();
    const result = await transport.current.startSession(firmName, firmToken, window.location.href, demoToken, undefined, source);
    if (!aliveRef.current) return;
    if (!result.ok) {
      // Chat brain unreachable → the outage form captures the lead instead.
      setFormVariant("outage");
      setPhase("form");
      return;
    }
    const notice: Message = {
      id: uid(), role: "system", text: `You're connected with a live assistant`, voice: false,
    };
    const greetMsg: Message = {
      id: uid(), role: "assistant", text: result.value.assistant_text, voice: false,
    };
    // Closed during the pacing beats → persist the greeting we already hold so
    // a reopen resumes a coherent conversation (not a blank "connected" line).
    const persistDead = () => {
      if (!demoToken) {
        saveChat(firmName, {
          sessionId: result.value.session_id, messages: [notice, greetMsg], ended: false,
        });
      }
    };
    setSessionId(result.value.session_id);
    setMessages([notice]);
    const wait = Math.max(0, minConnect - (Date.now() - t0));
    if (wait) await sleep(wait);
    if (!aliveRef.current) { persistDead(); return; }
    setPacing(true);
    setPhase("chat");

    await sleep(350);
    if (!aliveRef.current) { persistDead(); return; }
    setLoading(true);
    await sleep(900);
    if (!aliveRef.current) { persistDead(); return; }
    setLoading(false);
    setMessages((m) => [...m, greetMsg]);
    setPacing(false);
    if (result.value.choices) setCurrentChoices(result.value.choices);

    // If the visitor began via a starter prompt, send it as their first
    // message now that the session is live (sid passed explicitly — the
    // sendMessage closure predates setSessionId).
    const queued = pendingFirstMsg.current;
    if (queued) {
      pendingFirstMsg.current = null;
      void sendMessage(queued, false, { sid: result.value.session_id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmName, firmToken, demoToken, source, sendMessage]);

  // ── Intent-gated arrival: prechat submit → triage + contextual first reply ─
  const submitPrechat = useCallback(async (values: LeadFormValues) => {
    formValuesRef.current = values;
    ownSubmitRef.current = true;
    setFormError(null);
    setPhase("connecting");
    // Marker so a close-and-reopen mid-connect ADOPTS this submit instead of
    // offering a fresh form (duplicate lead). Cleared on every terminal path.
    if (!demoToken) markSubmitInFlight(firmName);
    const minConnect = 1200; // floor so a fast response doesn't flash the screen
    const t0 = Date.now();

    const result = await transport.current.startSession(
      firmName, firmToken, window.location.href, demoToken,
      undefined, source, values as PrechatValues,
    );

    if (result.ok) {
      if (!demoToken) {
        savePrechatContact(firmName, {
          first_name: values.first_name, last_name: values.last_name, phone: values.phone,
        });
      }
      // Old server (deploy skew) omits outcome → it opened a chat.
      const outcome = result.value.outcome ?? "chat";
      if (outcome === "captured") {
        if (!demoToken) {
          markCapturedThisTab(firmName);
          clearSubmitInFlight(firmName);
        }
        if (!aliveRef.current) return;
        setPhase("captured");
        return;
      }

      const arrival: Message[] = [
        { id: uid(), role: "system", text: `You're connected with a live assistant`, voice: false },
        { id: uid(), role: "user", text: values.inquiry, voice: false },
      ];
      // Deterministic welcome ("Hey {first}, my name is {rep} with {firm}…")
      // lands first, then a typing pause, then the contextual reply — the
      // greeting is instant while the real answer paces like a human typing.
      // Absent on an old server (deploy skew) → straight to the reply.
      const welcomeMsg: Message | null = result.value.welcome_text
        ? { id: uid(), role: "assistant", text: result.value.welcome_text, voice: false }
        : null;
      const assistantMsg: Message = {
        id: uid(), role: "assistant", text: result.value.assistant_text, voice: false,
      };
      // Persist the COMPLETE arrival (reply included) whenever the bubble is
      // closed before the pacing beats finish — the reply is already in hand,
      // and dropping it would resume a chat that looks abandoned while the
      // server transcript says it was answered.
      const persistDead = () => {
        if (!demoToken) {
          const closedNow = result.value.ended ?? false;
          saveChat(firmName, {
            sessionId: result.value.session_id,
            messages: [...arrival, ...(welcomeMsg ? [welcomeMsg] : []), assistantMsg],
            // A first-turn agent close persists as ended (+ the single-session
            // marker) so a reopen shows the ended conversation under the sheet.
            ended: closedNow,
            close_reason: closedNow ? (result.value.close_reason ?? "callback") : null,
          });
          if (closedNow) markSessionEnded(firmName, result.value.close_reason ?? "callback");
          clearSubmitInFlight(firmName);
        }
      };
      if (!aliveRef.current) {
        // Bubble closed mid-connect but the chat DID open server-side. Persist
        // it so reopening resumes this conversation instead of inviting a
        // duplicate submission (the orphan would otherwise reap as a second lead).
        persistDead();
        return;
      }
      setSessionId(result.value.session_id);
      // The visitor's inquiry renders as THEIR first bubble — the reply that
      // follows visibly responds to it. Synchronous save + clear the in-flight
      // marker only once the resumable state is durably written.
      setMessages(arrival);
      if (!demoToken) {
        saveChat(firmName, {
          sessionId: result.value.session_id, messages: arrival, ended: false,
        });
        clearSubmitInFlight(firmName);
      }
      const wait = Math.max(0, minConnect - (Date.now() - t0));
      if (wait) await sleep(wait);
      if (!aliveRef.current) { persistDead(); return; }
      setPacing(true);
      setPhase("chat");

      // Pacing beats (the generation work already happened behind the
      // connecting screen): a quick typing beat → the welcome greeting, then a
      // CLEARLY VISIBLE typing pause → the contextual reply, so the two bubbles
      // read like a person typing them one after the other, not one dump.
      if (welcomeMsg) {
        await sleep(400);
        if (!aliveRef.current) { persistDead(); return; }
        setLoading(true);
        await sleep(650);
        if (!aliveRef.current) { persistDead(); return; }
        setLoading(false);
        setMessages((m) => [...m, welcomeMsg]);
      }
      // Beat between the greeting and the reply: a short settle, then a long,
      // obvious typing indicator (~2.4s) before the answer lands.
      await sleep(welcomeMsg ? 700 : 350);
      if (!aliveRef.current) { persistDead(); return; }
      setLoading(true);
      await sleep(welcomeMsg ? 2400 : 650);
      if (!aliveRef.current) { persistDead(); return; }
      setLoading(false);
      setMessages((m) => [...m, assistantMsg]);
      setPacing(false);
      if (result.value.choices) setCurrentChoices(result.value.choices);
      // First-turn agent close (out-of-scope referral): the session is already
      // finalized server-side — lock the composer under the closing message.
      if (result.value.ended) {
        setCloseReason(result.value.close_reason ?? "callback");
        setEnded(true);
      }
      if (result.value.outcome === undefined) {
        // Old chat-service (deploy skew): the prechat block was ignored, so
        // the inquiry shown as the visitor's bubble never reached the model —
        // send it as the real first message now (no re-echo).
        void sendMessage(values.inquiry, false, {
          sid: result.value.session_id, echo: false,
        });
      }
      return;
    }

    // Degrade ladder: timeout / network / 429 / 5xx — the visitor already left
    // contact info, so capture it via the fallback endpoint. This POST runs
    // even if the bubble was closed mid-connect (lead > UI).
    const fb = await transport.current.sendFallbackMessage(
      firmName, firmToken, values as PrechatValues, window.location.href, source,
    );
    if (fb.ok) {
      if (!demoToken) {
        savePrechatContact(firmName, {
          first_name: values.first_name, last_name: values.last_name, phone: values.phone,
        });
        markCapturedThisTab(firmName);
        clearSubmitInFlight(firmName);
      }
      if (!aliveRef.current) return;
      setPhase("captured");
      return;
    }
    // Both paths down: back to the form with values intact + a retry.
    if (!demoToken) clearSubmitInFlight(firmName);
    if (!aliveRef.current) return;
    setFormError("We couldn't send your message just now — please try again.");
    setFormVariant("gate");
    setPhase("form");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmName, firmToken, demoToken, source, sendMessage]);

  // ── Outage form submit (legacy start failure): capture-only ────────────────
  const submitOutage = useCallback(async (values: LeadFormValues) => {
    formValuesRef.current = values;
    const fb = await transport.current.sendFallbackMessage(
      firmName, firmToken, values as PrechatValues, window.location.href, source,
    );
    if (!aliveRef.current) return;
    if (fb.ok) {
      if (!demoToken) markCapturedThisTab(firmName);
      setFormError(null);
      setPhase("captured");
      return;
    }
    setFormError("We couldn't send your message just now — please try again.");
  }, [firmName, firmToken, demoToken, source]);

  // Start chat / starter chip. Gate on: the form is the universal entry (chips
  // prefill the inquiry). Gate off: legacy straight-to-greeting flow.
  const beginChat = useCallback((prompt?: string) => {
    if (!gate) {
      if (prompt && prompt.trim()) pendingFirstMsg.current = prompt.trim();
      setPhase("connecting");
      void startBareSession();
      return;
    }
    setStarterPrefill(prompt?.trim() ?? "");
    setFormVariant("gate");
    setFormError(null);
    setPhase("form");
  }, [gate, startBareSession]);

  const onSelectChoice = useCallback(
    (choice: string) => {
      void sendMessage(choice, false);
    },
    [sendMessage],
  );

  // Re-send the last failed message without re-echoing the visitor's bubble.
  const retryLastMessage = useCallback(() => {
    if (retryText) void sendMessage(retryText, false, { echo: false });
  }, [retryText, sendMessage]);

  // Form prefill priority: values preserved from a failed submit (full set,
  // inquiry included) → saved contact + starter-chip inquiry.
  const formInitial: Partial<LeadFormValues> = formValuesRef.current ?? {
    ...(savedContact ?? {}),
    ...(starterPrefill ? { inquiry: starterPrefill } : {}),
  };
  const capturedName = formValuesRef.current?.first_name?.trim();

  return (
    <div class={`acc-window${ended ? " acc-window--ended" : ""}`} role="dialog" aria-label={`${title} chat`} tabIndex={-1} ref={rootRef}>
      <header class="acc-header">
        <span class="acc-header-avatar" aria-hidden="true">
          {heroImg ? <img src={heroImg} alt="" /> : initials}
        </span>
        <div class="acc-header-title">
          <strong>{headerLabel}</strong>
          <span class="acc-header-sub">Online now</span>
        </div>
        {!inline && (
          <button
            type="button"
            class="acc-header-close"
            onClick={onClose}
            aria-label="Close chat"
          >
            ×
          </button>
        )}
      </header>

      {phase === "welcome" ? (
        /* Welcome / cover screen — a clear, branded "start chat" entry point. */
        <div class="acc-welcome">
          <div class="acc-welcome-mark" aria-hidden="true">
            {heroImg ? <img src={heroImg} alt="" /> : initials}
          </div>

          <h2 class="acc-welcome-heading">{welcomeHeading}</h2>
          <p class="acc-welcome-sub">{welcomeSub}</p>

          {chips.length > 0 && (
            <div class="acc-welcome-trust">
              {chips.map((t) => (
                <span class="acc-trust-chip" key={t}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5"></path>
                  </svg>
                  {t}
                </span>
              ))}
            </div>
          )}

          <button
            type="button"
            class="acc-connect-btn acc-connect-btn--primary"
            onClick={() => beginChat()}
          >
            <span>Start chat</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7"></path>
            </svg>
          </button>

          {starters.length > 0 && (
            <div class="acc-welcome-starters">
              {starters.map((s) => (
                <button type="button" class="acc-starter-chip" key={s} onClick={() => beginChat(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <p class="acc-welcome-meta">Typically replies in minutes · Secure &amp; confidential · Not legal advice</p>
        </div>
      ) : phase === "form" ? (
        /* Pre-chat gate (or outage capture) — one LeadForm, two roles. */
        <LeadForm
          title={formVariant === "gate" ? "Connect with a live assistant" : "Leave a message"}
          intro={
            formVariant === "gate"
              ? formIntro?.trim() || DEFAULT_FORM_INTRO
              : `Our live assistants are unavailable right now. Leave your details below and someone from ${firmName} will get back to you.`
          }
          submitLabel={formVariant === "gate" ? "Connect now" : "Send message"}
          submittingLabel={formVariant === "gate" ? "Connecting…" : "Sending…"}
          initialValues={formInitial}
          error={formError}
          onSubmit={formVariant === "gate" ? submitPrechat : submitOutage}
        />
      ) : phase === "connecting" ? (
        /* Connecting screen — now covers REAL work: triage + the model reading
           the inquiry and composing the first reply. */
        <div class="acc-connecting" role="status" aria-live="polite">
          <div class="acc-connecting-avatar" aria-hidden="true">
            {heroImg ? <img src={heroImg} alt="" /> : initials}
          </div>
          <p class="acc-connecting-text">
            {connectingSlow ? "Reviewing your message…" : "Connecting you to a live assistant…"}
          </p>
          <p class="acc-connecting-sub">Secure &amp; confidential</p>
        </div>
      ) : phase === "captured" ? (
        /* Captured confirmation — same tone whether the triage gated it or the
           chat brain was down; never a hint that an AI judged the inquiry. The
           "Send another message" escape hatch matters: a same-tab reopen lands
           here (duplicate-submit guard), so there must be a way back in. */
        <div class="acc-fallback-success">
          <strong>Thanks{capturedName ? `, ${capturedName}` : ""} — we got your message.</strong>
          <p>
            Someone from {firmName} will reach out at the number you provided.
            If this is an emergency, please call 911.
          </p>
          {/* One resend per tab (founder directive: one message/session per
              visitor). The single retry stays so a typo'd phone number can be
              corrected; after that the affordance disappears. */}
          {(demoToken ||
            (typeof sessionStorage !== "undefined" &&
              !sessionStorage.getItem(`acc:resend-used:${firmName}`))) && (
            <button
              type="button"
              class="acc-captured-again"
              onClick={() => {
                if (!demoToken) {
                  try {
                    sessionStorage.setItem(`acc:resend-used:${firmName}`, "1");
                  } catch {
                    /* blocked storage — allow the resend rather than strand */
                  }
                  clearCapturedThisTab(firmName);
                  // Re-read NOW (not the mount-time value): the contact was
                  // saved during this instance's own submit.
                  setSavedContact(loadPrechatContact(firmName));
                }
                formValuesRef.current = null;
                setStarterPrefill(""); // never re-prefill the already-sent inquiry
                setFormVariant("gate");
                setFormError(null);
                setPhase("form");
              }}
            >
              Send another message
            </button>
          )}
        </div>
      ) : (
        /* Live conversation. .acc-chat-body is the positioning context for the
           ended sheet so it overlays the history without covering the
           inline-mode footer below. */
        <div class="acc-chat-body">
      <MessageList messages={messages} loading={loading} />

      {currentChoices && !ended && (
        <ChoiceChips
          prompt={currentChoices}
          onSelect={onSelectChoice}
          disabled={loading}
        />
      )}

      {error && !ended && (
        <div class="acc-error" role="alert">
          <span>{error}</span>
          {retryText && (
            <button type="button" class="acc-error-retry" onClick={retryLastMessage}>
              Try again
            </button>
          )}
        </div>
      )}

      {ended ? (
        /* Persistent ended sheet — no dismiss, and the composer is gone for
           good (the strongest form of disabled). It slides over the bottom of
           the STILL-SCROLLABLE history; the measuring effect above pads the
           message list by the sheet's height so the last bubble clears it. */
        <div class="acc-ended-sheet" ref={endedSheetRef} role="status" aria-live="polite">
          <p class="acc-ended-copy">{endedCopyFor(closeReason ?? "callback", title)}</p>
          {closeReason !== "ended" && (
            <span class="acc-ended-note">This chat is closed.</span>
          )}
        </div>
      ) : (
        <Composer
          disabled={loading || pacing || !sessionId}
          onSubmit={(t) => sendMessage(t)}
        />
      )}
        </div>
      )}

      {/* Footer only in inline mode (its "Start over" is the sole restart there;
          bubble mode closes via the header ×). */}
      {inline && (
        <footer class="acc-footer">
          <span class="acc-footer-text">
            Powered by Acceltra · Not legal advice ·{" "}
            <a href="#" onClick={(e) => { e.preventDefault(); onClose(); }}>
              Start over
            </a>
          </span>
        </footer>
      )}
    </div>
  );
}
